/**
 * Property API client for managing property submissions and media
 */

import { authenticatedRequest, ApiError } from '@/common/api/client'
import { config } from '@/config'
import { ANYWHERE_ELSE } from '@/common/api/markets'

// API base URL - from centralized config (required)
const API_BASE_URL = config.apiUrl

// =============================================================================
// Address Types (API format - snake_case)
// =============================================================================

/**
 * Full address data structure (API response)
 */
export interface PropertyAddress {
  id?: string
  street_address: string
  unit_number?: string | null
  city: string
  state: string
  zipcode: string
  latitude?: number
  longitude?: number
  full_address?: string
}

/**
 * Address data for creating a property
 */
export interface PropertyAddressCreate {
  street_address: string
  unit_number?: string | null
  city: string
  state: string
  zipcode: string
  latitude?: number
  longitude?: number
}

/**
 * Address data for updating a property (all fields optional)
 */
export interface PropertyAddressUpdate {
  street_address?: string
  unit_number?: string | null
  city?: string
  state?: string
  zipcode?: string
  latitude?: number
  longitude?: number
}

// =============================================================================
// Property Types
// =============================================================================

/**
 * Property condition enum values
 */
export type ConditionType = 'excellent' | 'good' | 'fair' | 'poor' | 'unknown'

/**
 * Property status enum values
 */
export type PropertyStatus = 'draft' | 'available' | 'under_contract' | 'sold' | 'removed'

/**
 * Property type enum values
 */
export type PropertyType = 'sfh' | 'multi_family' | 'condo' | 'townhome'

/**
 * Closing timeline enum values
 */
export type ClosingTimeline = '14_days' | '30_days' | '30_60_days' | 'flexible'

/**
 * Media type enum values
 */
export type MediaType = 'photo' | 'video'

/**
 * Media processing status enum values
 */
export type MediaStatus = 'processing' | 'ready' | 'failed'

/**
 * Open house date with time range
 */
export interface OpenHouseDate {
  date: string       // ISO date string "YYYY-MM-DD"
  start_time: string // 24-hour format "HH:MM"
  end_time: string   // 24-hour format "HH:MM"
}

/**
 * Property creation request data
 */
export interface PropertyCreate {
  address: PropertyAddressCreate
  property_type: PropertyType
  market: string
  beds?: number
  baths?: number
  sqft?: number
  year_built?: number
  market_value: number
  status?: PropertyStatus
  client?: string | null

  // Deal Hub integration (optional)
  deal_id?: number | null

  // MLS and CRM fields
  is_on_mls?: boolean
  follow_up_boss_link?: string | null

  // Condition fields
  overall_condition?: ConditionType
  overall_condition_notes?: string | null
  hvac_condition?: ConditionType
  hvac_condition_notes?: string | null
  roof_condition?: ConditionType
  roof_condition_notes?: string | null
  plumbing_condition?: ConditionType
  plumbing_condition_notes?: string | null
  electrical_condition?: ConditionType
  electrical_condition_notes?: string | null
  foundation_condition?: ConditionType
  foundation_condition_notes?: string | null
  // System ages (MRG-1008); *_unknown = agent answered "Don't know"
  hvac_install_year?: number | null
  hvac_install_year_unknown?: boolean
  roof_replacement_year?: number | null
  roof_replacement_year_unknown?: boolean
  water_heater_install_year?: number | null
  water_heater_install_year_unknown?: boolean
  additional_notes?: string | null

  // Pricing fields
  floor_price?: number | null
  share_market_value?: boolean
  share_floor_price?: boolean

  // Scheduling fields
  open_house_dates?: OpenHouseDate[] | null
  offer_deadline?: string | null  // ISO date string
  closing_timeline?: ClosingTimeline | null
}

/**
 * Property update request data (all fields optional except identifier)
 */
export interface PropertyUpdate {
  address?: PropertyAddressUpdate
  property_type?: PropertyType
  market?: string
  beds?: number
  baths?: number
  sqft?: number
  year_built?: number
  market_value?: number
  status?: PropertyStatus
  client?: string | null

  // Deal Hub integration (optional - can only be set once)
  deal_id?: number | null

  // MLS and CRM fields
  is_on_mls?: boolean
  follow_up_boss_link?: string | null

  // Condition fields
  overall_condition?: ConditionType
  overall_condition_notes?: string | null
  hvac_condition?: ConditionType
  hvac_condition_notes?: string | null
  roof_condition?: ConditionType
  roof_condition_notes?: string | null
  plumbing_condition?: ConditionType
  plumbing_condition_notes?: string | null
  electrical_condition?: ConditionType
  electrical_condition_notes?: string | null
  foundation_condition?: ConditionType
  foundation_condition_notes?: string | null
  // System ages (MRG-1008); *_unknown = agent answered "Don't know"
  hvac_install_year?: number | null
  hvac_install_year_unknown?: boolean
  roof_replacement_year?: number | null
  roof_replacement_year_unknown?: boolean
  water_heater_install_year?: number | null
  water_heater_install_year_unknown?: boolean
  additional_notes?: string | null

  // Pricing fields
  floor_price?: number | null
  share_market_value?: boolean
  share_floor_price?: boolean

  // Scheduling fields
  open_house_dates?: OpenHouseDate[] | null
  offer_deadline?: string | null  // ISO date string
  closing_timeline?: ClosingTimeline | null
}

/**
 * Property owner (agent) profile data
 */
export interface PropertyOwner {
  user_id: string
  first_name: string
  last_name: string
  public_email?: string | null
  public_phone?: string | null
}

/**
 * One entry in a property's agent assignment history (admin-only).
 */
export interface OwnerHistoryEntry {
  agent_id: string
  assigned_at: string
}

/**
 * Property response data
 */
export interface PropertyResponse {
  id: string
  address: PropertyAddress
  property_type: PropertyType
  market: string
  beds?: number
  baths?: number
  sqft?: number
  year_built?: number
  market_value: number | null  // null when investor lacks access (gated field)
  status: PropertyStatus
  owner_id: string
  owner?: PropertyOwner | null
  /** Agent assignment history, oldest first (admin-only) */
  owner_history?: OwnerHistoryEntry[] | null
  submitted_at?: string | null
  client?: string | null

  // MLS and CRM fields (agent-only)
  is_on_mls?: boolean
  follow_up_boss_link?: string | null

  // Condition fields
  overall_condition?: ConditionType
  overall_condition_notes?: string | null
  hvac_condition?: ConditionType
  hvac_condition_notes?: string | null
  roof_condition?: ConditionType
  roof_condition_notes?: string | null
  plumbing_condition?: ConditionType
  plumbing_condition_notes?: string | null
  electrical_condition?: ConditionType
  electrical_condition_notes?: string | null
  foundation_condition?: ConditionType
  foundation_condition_notes?: string | null
  // System ages (MRG-1008); *_unknown = agent answered "Don't know"
  hvac_install_year?: number | null
  hvac_install_year_unknown?: boolean
  roof_replacement_year?: number | null
  roof_replacement_year_unknown?: boolean
  water_heater_install_year?: number | null
  water_heater_install_year_unknown?: boolean
  additional_notes?: string | null

  // Pricing fields
  floor_price?: number | null
  share_market_value?: boolean
  share_floor_price?: boolean
  house_canary_valuation?: number | null  // AVM estimate from House Canary

  // Scheduling fields
  open_house_dates?: OpenHouseDate[] | null
  offer_deadline?: string | null  // ISO date string
  closing_timeline?: ClosingTimeline | null

  // Timestamps
  created_at: string
  updated_at: string

  // Related data
  media?: PropertyMediaResponse[]

  // Thumbnail (first photo URL ordered by display_order)
  thumbnail?: string | null

  // Offer counts (agent-only fields)
  // offer_count = pending + engaged + accepted (excludes withdrawn/rejected)
  offer_count?: number
  pending_offer_count?: number
  accepted_offer_count?: number
  rejected_offer_count?: number
  withdrawn_offer_count?: number

  // Questions count (agent-only field) - number of information requests on this property
  questions_count?: number

  // Offer status (investor-only field) - status of investor's latest offer on this property
  offer_status?: 'pending' | 'engaged' | 'accepted' | 'rejected' | 'withdrawn' | null

  // Deal Hub integration data (agent-only)
  deal_hub_data?: PropertyDealHubData | null

  // Whether the owner is an external (third-party) agent (returned only when selected;
  // serializes as null when present but not hydrated)
  is_external_submission?: boolean | null
  /**
   * True once a sold/removed outcome has been recorded, locking the property's
   * status and its offers' statuses (MRG-926). Virtual field — must be named
   * explicitly in `select`, it is not covered by `@all_fields`.
   */
  has_terminal_outcome?: boolean | null
  /**
   * Buyer named when the property was moved to Under Contract, returned only
   * while it is still under contract (MRG-926). The Sold capture confirms this
   * buyer instead of asking the agent to retype it.
   */
  open_under_contract_outcome?: {
    winner_kind: 'offer' | 'inquiry' | 'free_text_investor' | 'outside'
    free_text_investor_name?: string | null
    outside_reason?: string | null
    outside_partner?: string | null
    outside_other_text?: string | null
    recorded_at: string
  } | null
}

/**
 * Deal Hub data stored with a property
 */
export interface PropertyDealHubData {
  id: string
  deal_id: number
  transaction_property_id: number | null
  latest_booked_date: string | null
  listed_date: string | null
  under_contract_date: string | null
  closing_date: string | null
  buyer_names: string[] | null
  dh_beds: number | null
  dh_baths: number | null
  dh_year_built: number | null
  dh_property_type: string | null
  dh_client: string | null
  agent_modified: boolean
  synced_at: string | null
  created_at: string
  updated_at: string
}

/**
 * Property media response data
 */
export interface PropertyMediaResponse {
  id: string
  property_id: string
  media_type: MediaType
  file_url: string
  file_size: number
  display_order: number
  uploaded_at: string

  // Media processing fields
  status: MediaStatus
  thumbnail_url: string | null
  processed_at: string | null
  error_message: string | null
  caption: string | null
}

/**
 * Paginated response wrapper from API
 */
export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    page_size: number
    total_count: number
    total_pages: number
    has_more: boolean
  }
}

/**
 * Parameters for listing properties with full query capabilities
 */
export interface ListPropertiesParams {
  /** Page number (1-indexed) */
  page?: number
  /** Items per page (max 100) */
  pageSize?: number
  /** Field to sort by */
  sortBy?: string
  /** Sort direction */
  sortDir?: 'asc' | 'desc'
  /** Filter by statuses (multiple) */
  statuses?: PropertyStatus[]
  /** Filter by markets (multiple) */
  markets?: string[]
  /** Filter by property types (multiple) */
  property_types?: string[]
  /** Filter by zipcodes (multiple) */
  zipcodes?: string[]
  /** Search query (searches address components) */
  search?: string
  /** Minimum market value */
  min_market_value?: number
  /** Maximum market value */
  max_market_value?: number
  /** Filter by overall condition */
  condition?: string
  /** Filter by plumbing condition */
  plumbing_condition?: string
  /** Filter by electrical condition */
  electrical_condition?: string
  /** Filter by foundation condition */
  foundation_condition?: string
  /** Minimum bedrooms */
  min_beds?: number
  /** Maximum bedrooms */
  max_beds?: number
  /** Minimum bathrooms */
  min_baths?: number
  /** Maximum bathrooms */
  max_baths?: number
  /** Minimum square footage */
  min_sqft?: number
  /** Maximum square footage */
  max_sqft?: number
  /** Minimum year built */
  min_year_built?: number
  /** Maximum year built */
  max_year_built?: number
  /** Minimum AVM (House Canary valuation) */
  min_avm_value?: number
  /** Maximum AVM (House Canary valuation) */
  max_avm_value?: number
  /** Filter by client name (exact match) */
  client?: string
  /** Filter by submitted_at date (minimum, ISO string) */
  submitted_at_gte?: string
  /** Filter by submitted_at date (maximum, ISO string) */
  submitted_at_lte?: string
  /** Filter by offer_deadline (minimum, supports "today", "now", or ISO string) */
  offer_deadline_gte?: string
  /** Filter by offer_deadline (maximum, supports "today", "now", or ISO string) */
  offer_deadline_lte?: string
  /** Fields to select/return from the API */
  select?: string[]
  /** Filter by MLS listing status */
  is_on_mls?: boolean
  /** Include all properties (bypass default filter that excludes removed) */
  includeAll?: boolean
  /** Filter for properties with accepted offers not yet under contract */
  has_accepted_offer?: boolean
  /**
   * MRG-281: when true, the server returns hardcoded training fixtures
   * instead of querying the DB. The flag is silently ignored unless the
   * caller is an external agent who hasn't completed training. UI code
   * should derive this from `useUserProfile().needsTraining` plus
   * `agent_type === 'external'`.
   */
  training_mode?: boolean
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * A filter condition - either simple or compound (or/and)
 */
type FilterCondition =
  | { field: string; op: string; value: unknown }
  | { or: FilterCondition[] }
  | { and: FilterCondition[] }

/** Market selection; Anywhere else also matches every Other area ("OTH_ID") by prefix */
function marketFilter(markets: string[]): FilterCondition {
  const marketIn: FilterCondition = { field: 'market', op: 'in', value: markets }
  if (!markets.includes(ANYWHERE_ELSE)) return marketIn
  return { or: [marketIn, { field: 'market', op: 'like', value: 'OTH_%' }] }
}

/**
 * Build a JSON filter expression for the v2 API
 */
function buildFilterJson(filters: FilterCondition[]): string | null {
  if (filters.length === 0) return null
  if (filters.length === 1) return JSON.stringify(filters[0])
  return JSON.stringify({ and: filters })
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Create a new property
 */
export async function createProperty(
  token: string,
  data: PropertyCreate
): Promise<PropertyResponse> {
  return authenticatedRequest<PropertyResponse>('/properties', token, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * List properties with full query capabilities (pagination, sorting, filtering).
 */
export async function listProperties(
  token: string,
  params: ListPropertiesParams = {}
): Promise<PaginatedResponse<PropertyResponse>> {
  const urlParams = new URLSearchParams()

  // Pagination params
  if (params.page !== undefined) {
    urlParams.append('page', String(params.page))
  }
  if (params.pageSize !== undefined) {
    urlParams.append('pageSize', String(params.pageSize))
  }

  // Sort params
  if (params.sortBy) {
    urlParams.append('sortBy', params.sortBy)
  }
  if (params.sortDir) {
    urlParams.append('sortDir', params.sortDir)
  }

  // Search param (server-side address search)
  if (params.search) {
    urlParams.append('search', params.search)
  }

  // Build filter conditions
  const filters: FilterCondition[] = []

  if (params.statuses && params.statuses.length > 0) {
    filters.push({ field: 'status', op: 'in', value: params.statuses })
  }
  if (params.markets && params.markets.length > 0) {
    filters.push(marketFilter(params.markets))
  }
  if (params.property_types && params.property_types.length > 0) {
    filters.push({ field: 'property_type', op: 'in', value: params.property_types })
  }
  if (params.zipcodes && params.zipcodes.length > 0) {
    filters.push({ field: 'address.zipcode', op: 'in', value: params.zipcodes })
  }
  if (params.min_market_value !== undefined) {
    filters.push({ field: 'market_value', op: 'gte', value: params.min_market_value })
  }
  if (params.max_market_value !== undefined) {
    filters.push({ field: 'market_value', op: 'lte', value: params.max_market_value })
  }
  if (params.condition) {
    filters.push({ field: 'overall_condition', op: 'eq', value: params.condition })
  }
  if (params.plumbing_condition) {
    filters.push({ field: 'plumbing_condition', op: 'eq', value: params.plumbing_condition })
  }
  if (params.electrical_condition) {
    filters.push({ field: 'electrical_condition', op: 'eq', value: params.electrical_condition })
  }
  if (params.foundation_condition) {
    filters.push({ field: 'foundation_condition', op: 'eq', value: params.foundation_condition })
  }
  if (params.min_beds !== undefined) {
    filters.push({ field: 'beds', op: 'gte', value: params.min_beds })
  }
  if (params.max_beds !== undefined) {
    filters.push({ field: 'beds', op: 'lte', value: params.max_beds })
  }
  if (params.min_baths !== undefined) {
    filters.push({ field: 'baths', op: 'gte', value: params.min_baths })
  }
  if (params.max_baths !== undefined) {
    filters.push({ field: 'baths', op: 'lte', value: params.max_baths })
  }
  if (params.min_sqft !== undefined) {
    filters.push({ field: 'sqft', op: 'gte', value: params.min_sqft })
  }
  if (params.max_sqft !== undefined) {
    filters.push({ field: 'sqft', op: 'lte', value: params.max_sqft })
  }
  if (params.min_year_built !== undefined) {
    filters.push({ field: 'year_built', op: 'gte', value: params.min_year_built })
  }
  if (params.max_year_built !== undefined) {
    filters.push({ field: 'year_built', op: 'lte', value: params.max_year_built })
  }
  if (params.min_avm_value !== undefined) {
    filters.push({ field: 'house_canary_valuation', op: 'gte', value: params.min_avm_value })
  }
  if (params.max_avm_value !== undefined) {
    filters.push({ field: 'house_canary_valuation', op: 'lte', value: params.max_avm_value })
  }
  if (params.client) {
    filters.push({ field: 'client', op: 'eq', value: params.client })
  }
  if (params.submitted_at_gte) {
    filters.push({ field: 'submitted_at', op: 'gte', value: params.submitted_at_gte })
  }
  if (params.submitted_at_lte) {
    filters.push({ field: 'submitted_at', op: 'lte', value: params.submitted_at_lte })
  }
  if (params.offer_deadline_gte) {
    // Include properties with deadline >= value OR no deadline (NULL = always valid)
    filters.push({
      or: [
        { field: 'offer_deadline', op: 'gte', value: params.offer_deadline_gte },
        { field: 'offer_deadline', op: 'null', value: true },
      ],
    })
  }
  if (params.offer_deadline_lte) {
    filters.push({ field: 'offer_deadline', op: 'lte', value: params.offer_deadline_lte })
  }
  if (params.is_on_mls !== undefined) {
    filters.push({ field: 'is_on_mls', op: 'eq', value: params.is_on_mls })
  }

  // Select param
  if (params.select && params.select.length > 0) {
    urlParams.append('select', params.select.join(','))
  }

  const filterJson = buildFilterJson(filters)
  if (filterJson) {
    urlParams.append('filter', filterJson)
  }

  // Include all (bypass default filter that excludes removed properties)
  if (params.includeAll) {
    urlParams.append('includeAll', 'true')
  }

  // Has accepted offer filter (server-side EXISTS subquery)
  if (params.has_accepted_offer) {
    urlParams.append('has_accepted_offer', 'true')
  }

  // MRG-281 training mode (server returns fixtures for in-training external agents)
  if (params.training_mode) {
    urlParams.append('training_mode', 'true')
  }

  const queryString = urlParams.toString()
  const url = queryString ? `/properties?${queryString}` : '/properties'
  return authenticatedRequest<PaginatedResponse<PropertyResponse>>(url, token)
}

/**
 * Options for getting a single property
 */
export interface GetPropertyOptions {
  /** Fields to select. Use PropertySelect presets or custom array. */
  select?: readonly string[] | string[]
  /**
   * MRG-281: when true, the server returns the training-demo property
   * fixture (only valid for the demo property ID + an in-training
   * external agent — silently ignored otherwise).
   */
  trainingMode?: boolean
}

/**
 * Presets for common property select patterns.
 *
 * - `detail`: All fields + media (for details page)
 * - `edit`: All fields + full address (with lat/lon) + media (for edit form)
 * - `minimal`: Basic fields only (for lists, cards)
 */
export const PropertySelect = {
  /**
   * All selectable fields plus media and owner (for detail views).
   *
   * Must stay free of role-gated fields. `@all_fields` silently drops fields the
   * caller's role cannot select, but a field named explicitly is validated and
   * raises 403 for everyone else — so naming an agent-only field here fails the
   * whole request for investors rather than omitting it (MRG-926 regression).
   */
  detail: ['@all_fields', 'media', 'owner.@all_fields'],
  /**
   * Detail view for agents/admins: adds the outcome fields behind the status
   * badge's locked state (MRG-926). Both are `roles: ["agent"]`, so this preset
   * is only safe when the viewer can change status.
   */
  detailWithOutcome: [
    '@all_fields',
    'media',
    'owner.@all_fields',
    'has_terminal_outcome',
    'open_under_contract_outcome',
  ],
  /** All fields plus full nested address (with lat/lon) plus media plus deal hub data (for edit form) */
  edit: ['@all_fields', 'address.@all_fields', 'media', 'deal_hub_data', 'is_external_submission'],
  /** Minimal fields for lists/cards */
  minimal: ['address', 'status', 'market', 'market_value', 'property_type', 'beds', 'baths', 'sqft', 'thumbnail'],
} as const

/**
 * Query key for a single property's detail fetch.
 *
 * The trailing variant is load-bearing: agents/admins fetch `detailWithOutcome`
 * and everyone else fetches `detail`, and the two payloads differ. Sharing one
 * key would let an agent read a cache entry seeded by a `detail` fetch and see
 * an unlocked status badge on a locked property.
 *
 * Keeps the `['property', id]` prefix so existing prefix invalidations match.
 * Anything writing this cache with an exact key (setQueryData) must build it
 * here rather than by hand.
 */
export function propertyDetailQueryKey(
  id: string,
  withOutcome: boolean
): readonly [string, string, string] {
  return ['property', id, withOutcome ? 'with-outcome' : 'base']
}

/**
 * Get a specific property by ID
 *
 * @param token - JWT access token
 * @param id - Property ID
 * @param options - Optional configuration (select fields)
 *
 * @example
 * // Get property with all fields + media (default)
 * const property = await getProperty(token, id)
 *
 * @example
 * // Get property for edit form (includes address lat/lon)
 * const property = await getProperty(token, id, { select: PropertySelect.edit })
 *
 * @example
 * // Get property with minimal fields
 * const property = await getProperty(token, id, { select: PropertySelect.minimal })
 */
export async function getProperty(
  token: string,
  id: string,
  options?: GetPropertyOptions
): Promise<PropertyResponse> {
  const params = new URLSearchParams()
  // Default to detail preset if no select specified
  const selectFields = options?.select ?? PropertySelect.detail
  params.append('select', selectFields.join(','))
  if (options?.trainingMode) {
    params.append('training_mode', 'true')
  }
  return authenticatedRequest<PropertyResponse>(`/properties/${id}?${params}`, token)
}

/**
 * Update an existing property
 */
export async function updateProperty(
  token: string,
  id: string,
  data: PropertyUpdate
): Promise<PropertyResponse> {
  return authenticatedRequest<PropertyResponse>(`/properties/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Reassign a property to a different agent (admin only).
 *
 * The previous agent loses access immediately and the new agent is emailed.
 */
// =============================================================================
// Outcome capture (MRG-926)
// =============================================================================

export type OutcomeTargetStatus = 'sold' | 'removed' | 'under_contract'
export type OutcomeWinnerKind = 'offer' | 'inquiry' | 'free_text_investor' | 'outside'

export interface PropertyOutcomeCreate {
  terminal_status: OutcomeTargetStatus
  winner_kind: OutcomeWinnerKind
  winning_offer_id?: string | null
  winning_inquiry_id?: string | null
  free_text_investor_name?: string | null
  outside_reason?: string | null
  outside_partner?: string | null
  outside_other_text?: string | null
  /**
   * Whether the agent advanced past the upload step without a file. The server
   * re-derives whether the step applied at all and overrides this, so it is a
   * hint rather than a source of truth.
   */
}

export interface PropertyOutcomeResponse {
  id: string
  property_id: string
  terminal_status: OutcomeTargetStatus
  winner_kind: OutcomeWinnerKind
  winning_offer_id: string | null
  winning_inquiry_id: string | null
  free_text_investor_name: string | null
  outside_reason: string | null
  outside_partner: string | null
  outside_other_text: string | null
  would_work_again: string | null
  orchard_rating: number | null
  feedback_note: string | null
  feedback_submitted_at: string | null
  created_by: string
  created_at: string
  /** Offers resolved by this capture — drives the confirmation copy. */
  offers_accepted: number
  offers_rejected: number
}

export interface PropertyOutcomeFeedback {
  would_work_again?: string | null
  orchard_rating?: number | null
  feedback_note?: string | null
}

/**
 * Record a manual outcome, applying the status change and resolving the
 * property's offers atomically.
 *
 * Rejects with 409 when a terminal (sold/removed) outcome already exists.
 * Existing `under_contract` captures never block — a deal can fall through and
 * the property can go under contract again with a different investor.
 */
export async function recordPropertyOutcome(
  token: string,
  propertyId: string,
  data: PropertyOutcomeCreate
): Promise<PropertyOutcomeResponse> {
  return authenticatedRequest<PropertyOutcomeResponse>(
    `/properties/${propertyId}/outcome`,
    token,
    { method: 'POST', body: JSON.stringify(data) }
  )
}

/**
 * Attach the optional feedback answer to an already-committed outcome.
 *
 * This is the one write permitted against a locked property — the outcome is
 * already recorded by the time the feedback screen is reachable, so closing
 * without answering costs nothing.
 */
export async function submitOutcomeFeedback(
  token: string,
  propertyId: string,
  data: PropertyOutcomeFeedback
): Promise<PropertyOutcomeResponse> {
  return authenticatedRequest<PropertyOutcomeResponse>(
    `/properties/${propertyId}/outcome/feedback`,
    token,
    { method: 'PATCH', body: JSON.stringify(data) }
  )
}

export async function reassignProperty(
  token: string,
  id: string,
  newOwnerId: string
): Promise<PropertyResponse> {
  return authenticatedRequest<PropertyResponse>(`/properties/${id}/reassign`, token, {
    method: 'POST',
    body: JSON.stringify({ new_owner_id: newOwnerId }),
  })
}

/**
 * Delete a property
 */
export async function deleteProperty(token: string, id: string): Promise<void> {
  await authenticatedRequest<void>(`/properties/${id}`, token, {
    method: 'DELETE',
  })
}

/**
 * Upload media file for a property.
 * When onProgress is provided, uses XMLHttpRequest for real-time upload progress tracking.
 */
export async function uploadPropertyMedia(
  token: string,
  propertyId: string,
  file: File,
  mediaType: MediaType = 'photo',
  options?: {
    onProgress?: (percent: number) => void
    caption?: string
  }
): Promise<PropertyMediaResponse> {
  const formData = new FormData()
  formData.append('file', file)
  if (options?.caption) {
    formData.append('caption', options.caption)
  }

  const url = `${API_BASE_URL}/properties/${propertyId}/media?media_type=${mediaType}`

  // Use XMLHttpRequest when progress tracking is needed
  if (options?.onProgress) {
    return new Promise<PropertyMediaResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100)
          options.onProgress!(percent)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status === 201) {
          resolve(JSON.parse(xhr.responseText))
        } else if (xhr.status === 401) {
          reject(new ApiError('Unauthorized - session expired', 401))
        } else if (xhr.status === 403) {
          reject(new ApiError('Forbidden - insufficient permissions', 403))
        } else if (xhr.status >= 500) {
          reject(new ApiError('Service unavailable', xhr.status))
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText)
            const message = errorData.detail || `Upload failed: ${xhr.statusText}`
            reject(new ApiError(message, xhr.status, errorData))
          } catch {
            reject(new ApiError(`Upload failed: ${xhr.statusText}`, xhr.status))
          }
        }
      })

      xhr.addEventListener('error', () => {
        reject(new ApiError('Network error - unable to reach API'))
      })

      xhr.open('POST', url)
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      xhr.send(formData)
    })
  }

  // Standard fetch path (no progress tracking)
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    })

    if (response.status === 401) {
      throw new ApiError('Unauthorized - session expired', 401)
    }

    if (response.status === 403) {
      throw new ApiError('Forbidden - insufficient permissions', 403)
    }

    if (response.status >= 500) {
      throw new ApiError('Service unavailable', response.status)
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      // Extract detail message from FastAPI error response (e.g., "Image file too large...")
      const message = errorData.detail || `Upload failed: ${response.statusText}`
      throw new ApiError(message, response.status, errorData)
    }

    return await response.json()
  } catch (error) {
    if (error instanceof TypeError) {
      throw new ApiError('Network error - unable to reach API', undefined, error)
    }
    if (error instanceof ApiError) {
      throw error
    }
    throw new ApiError('Unknown error occurred', undefined, error)
  }
}

/**
 * Get all media for a property
 */
export async function getPropertyMedia(
  token: string,
  propertyId: string
): Promise<PropertyMediaResponse[]> {
  return authenticatedRequest<PropertyMediaResponse[]>(
    `/properties/${propertyId}/media`,
    token
  )
}

/**
 * Delete a media item from a property
 */
export async function deletePropertyMedia(
  token: string,
  propertyId: string,
  mediaId: string
): Promise<void> {
  await authenticatedRequest<void>(
    `/properties/${propertyId}/media/${mediaId}`,
    token,
    {
      method: 'DELETE',
    }
  )
}

/**
 * Media reorder item for bulk update
 */
export interface MediaReorderItem {
  id: string
  display_order: number
}

/**
 * Update property media caption
 */
export async function updatePropertyMediaCaption(
  token: string,
  propertyId: string,
  mediaId: string,
  caption: string | null
): Promise<PropertyMediaResponse> {
  return authenticatedRequest<PropertyMediaResponse>(
    `/properties/${propertyId}/media/${mediaId}`,
    token,
    {
      method: 'PATCH',
      body: JSON.stringify({ caption }),
    }
  )
}

/**
 * Reorder property media items
 */
export async function reorderPropertyMedia(
  token: string,
  propertyId: string,
  mediaOrder: MediaReorderItem[]
): Promise<PropertyMediaResponse[]> {
  return authenticatedRequest<PropertyMediaResponse[]>(
    `/properties/${propertyId}/media/reorder`,
    token,
    {
      method: 'PATCH',
      body: JSON.stringify({ media_order: mediaOrder }),
    }
  )
}

// =============================================================================
// Information Request Types & API
// =============================================================================

/**
 * Information request response data
 */
export interface InfoRequestResponse {
  id: string
  property_id: string
  investor_id: string
  investor_email: string
  investor_name?: string | null
  company_name?: string | null
  message: string
  created_at: string
  message_count: number
}

/**
 * Submit an information request (question) for a property
 *
 * @param token - JWT access token
 * @param propertyId - Property ID to request info about
 * @param message - Investor's question (max 1000 chars)
 * @returns Created information request
 */
export async function requestPropertyInfo(
  token: string,
  propertyId: string,
  message: string
): Promise<InfoRequestResponse> {
  return authenticatedRequest<InfoRequestResponse>(
    `/properties/${propertyId}/request-info`,
    token,
    {
      method: 'POST',
      body: JSON.stringify({ message }),
    }
  )
}

/**
 * List the information requests (inquiries) on a property.
 *
 * Scoped server-side by role: agents see inquiries on their own properties,
 * admins see inquiries on any property. Used by the property side panel.
 *
 * @param token - JWT access token
 * @param propertyId - Property ID to list inquiries for
 * @returns Inquiries on the property, newest first
 */
export async function listPropertyInquiries(
  token: string,
  propertyId: string
): Promise<InfoRequestResponse[]> {
  return authenticatedRequest<InfoRequestResponse[]>(
    `/information-requests?property_id=${encodeURIComponent(propertyId)}`,
    token
  )
}
